import { type FailedResponse, type SuccessResponseWithoutPayload } from './types'
import { ApiUtil } from './api-utils'
import { mapDeviceMeterDataHistoryResponseItems, mapDeviceResponseItem, mapDeviceResponseItems } from './response-mappers'
import { type DeviceMeterDataHistoryResponseItem, type DeviceResponseItem } from './device-api-types'

/* API-related constants */
const DEVICES_API_PATH = '/device'

/* Response types */

export type DeviceDeleteResponse = SuccessResponseWithoutPayload | FailedResponse

interface DeviceHistorySuccessResponse {
  succeeded: true
  payload: {
    items: DeviceMeterDataHistoryResponseItem[]
  }
}

export type DeviceHistoryResponse = DeviceHistorySuccessResponse | FailedResponse

interface DeviceUpdateSuccessResponse {
  succeeded: true
  payload: {
    device: DeviceResponseItem
  }
}

export type DeviceUpdateResponse = DeviceUpdateSuccessResponse | FailedResponse

interface GetDevicesSuccessResponse {
  succeeded: true
  payload: {
    devices: DeviceResponseItem[]
  }
}

export type GetDevicesResponse = GetDevicesSuccessResponse | FailedResponse

/* Request payload types */

/**
 * Fetch meter history query call related query params.
 * @param start Start date as Epoch seconds.
 * @param end End date as Epoch seconds.
 */
export interface FetchMeterHistoryQueryParams {
  start: number
  end: number
}

export interface UpdateMeterRequestPayload {
  alarmSettings: {
    backwardFlowEnabled: boolean
    batteryWarningEnabled: boolean
    emptyTubeDetectedEnabled: boolean
    flowLimitEnabled: boolean
    fraudDetectedEnabled: boolean
    freezeDetectedEnabled: boolean
    hardwareErrorEnabled: boolean
    leakageEnabled: boolean
    magneticFraudEnabled: boolean
    maxFlowPerHour?: number
    moduleRemovedEnabled: boolean
    opticalFraudEnabled: boolean
    resetDetectedEnabled: boolean
    switchErrorEnabled: boolean
    tooBigFlowEnabled: boolean
  }
  baseValue?: number
  labels: string[]
  location?: string
  pulseInLiters?: number
  serialNumber?: string
  transmitterSerialNumber?: string
}

/* Export(s) */

/**
 * Backend API for admin device provisions.
 */
export const DeviceApi = {
  deleteDevice,
  getDeviceHistoryValues,
  getDevices,
  updateDevice
}

export type DeviceApiType = typeof DeviceApi

/* API methods */

/**
 * Delete an existing device.
 * @param token Bearer token.
 * @param deviceId ID of the device to be removed.
 */
async function deleteDevice(
  token: string,
  deviceId: string
): Promise<DeviceDeleteResponse> {
  const apiDesc = 'delete device request'
  const url = ApiUtil.getApiUrl(`${DEVICES_API_PATH}/${deviceId}`)

  return await ApiUtil.execDeleteRequest(url, token, apiDesc)
}

/**
 * Get device data history values.
 * @param token Bearer token.
 * @param deviceId Device ID.
 * @param input Query input params.
 */
async function getDeviceHistoryValues(
  token: string,
  deviceId: string,
  input: FetchMeterHistoryQueryParams
): Promise<DeviceHistoryResponse> {
  const apiDesc = 'get device history values request'
  const queryParams = `start=${input.start}&end=${input.end}`
  const url = ApiUtil.getApiUrl(`${DEVICES_API_PATH}/${deviceId}/history?${queryParams}`)

  try {
    const response = await ApiUtil.doGetRequest(url, token)
    if (!response.ok) {
      return await ApiUtil.errorResponse(response, apiDesc)
    }
    const payload: unknown = await response.json()
    const parsedPayload = mapDeviceMeterDataHistoryResponseItems(payload)
    return {
      succeeded: true,
      payload: {
        items: parsedPayload
      }
    }
  } catch (e) {
    return ApiUtil.unknownExceptionResponse(e, url, apiDesc)
  }
}

/**
 * Get devices.
 * @param token Bearer token.
 */
async function getDevices(
  token: string
): Promise<GetDevicesResponse> {
  const apiDesc = 'get devices request'
  const url = ApiUtil.getApiUrl(DEVICES_API_PATH)

  try {
    const response = await ApiUtil.doGetRequest(url, token)
    if (!response.ok) {
      return await ApiUtil.errorResponse(response, apiDesc)
    }
    const payload: unknown = await response.json()
    const parsedPayload = mapDeviceResponseItems(payload)
    return {
      succeeded: true,
      payload: {
        devices: parsedPayload
      }
    }
  } catch (e) {
    return ApiUtil.unknownExceptionResponse(e, url, apiDesc)
  }
}

/**
 * Update existing device.
 * @param token Bearer token.
 * @param deviceId ID of the device to be removed.
 * @param input Input data for the device update.
 */
async function updateDevice(
  token: string,
  deviceId: string,
  input: UpdateMeterRequestPayload
): Promise<DeviceUpdateResponse> {
  const apiDesc = 'update device request'
  const url = ApiUtil.getApiUrl(`${DEVICES_API_PATH}/${deviceId}`)

  const requestBody = { ...input }

  try {
    const response = await ApiUtil.doPatchRequest(url, requestBody, token)
    if (!response.ok) {
      return await ApiUtil.errorResponse(response, apiDesc)
    }
    const payload: unknown = await response.json()
    const parsedPayload = mapDeviceResponseItem(payload)
    return {
      succeeded: true,
      payload: {
        device: parsedPayload
      }
    }
  } catch (e) {
    return ApiUtil.unknownExceptionResponse(e, url, apiDesc)
  }
}
